// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import PaginationArrowLeft from "@steiner/XDL/Icons/PaginationArrowLeft";
import PaginationArrowRight from "@steiner/XDL/Icons/PaginationArrowRight";
import { buttonTransparent } from "@steiner/XDL/PreStyle/button";
import { xBaseTheme } from "@steiner/XDL/types";
import { color, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

const selectPropsStyles = {
    label: {
        width: 46,
    },
    input: {
        width: 58,
        paddingRight: 15,
    },
};

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            mergeCss: true,
            labelProps: {
                mergeCss: true,
                size: 14,
                weight: "normal",
                css: css`
                    letter-spacing: 0;
                    line-height: 14px;
                    min-height: initial;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: ${selectPropsStyles.input.width +
                    selectPropsStyles.input.paddingRight +
                    selectPropsStyles.label.width}px;
                    padding-right: 0;
                    min-width: 104px;
                `,
                formInputWrapper: css`
                    width: calc(100% - ${selectPropsStyles.label.width}px);
                    max-width: ${selectPropsStyles.input.width}px;
                    ${IconWrapper} > svg {
                        height: 12px;
                        width: 12px;
                    }
                `,
            },
            css: css`
                &&&&&& {
                    padding-right: 24px;
                }
            `,
        },
        paginationContainer: {
            css: css`
                &&& {
                    display: inline-flex;
                    align-items: baseline;
                    gap: 8px;
                    margin: auto;

                    & > ${TypographyStyle} {
                        font-size: 14px;
                        font-weight: 400;
                        white-space: nowrap;
                    }
                }
            `,
        },
        paginationSelect: {
            sizeVariant: "small",
            cssOverrides: {
                formField: css`
                    width: auto;
                    min-width: 35px;
                    max-width: 62px;
                    &&&& select {
                        padding-right: 24px;
                    }
                `,
            },
        },

        cssOverrides: {
            pagination: css`
                ${xScreen("lte", "md")} {
                    width: 100%;
                    justify-content: space-between;
                }
            `,
            linkList: css`
                ul {
                    display: flex;
                    gap: 8px;
                    align-items: center;

                    & > li.previousPage,
                    & > li.nextPage {
                        display: flex;
                        align-items: center;
                    }
                }
                ${xScreen("lte", "md")} {
                    margin-left: auto;
                }

                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
            perPageSelect: css`
                ${xScreen("lte", "md")} {
                    width: auto;
                }
            `,
        },
        buttonProps: {
            css: css`
                ${buttonTransparent}

                &&&& {
                    width: 24px;
                }

                &[data-test-selector="pagination_previousButton"],
                &[data-test-selector="pagination_nextButton"] {
                    margin: 0;

                    ${IconWrapper} {
                        top: 0;
                        &,
                        & > svg {
                            width: 11px;
                        }
                    }
                }

                &:not(:disabled) > ${IconWrapper} {
                    color: ${color("common.ui4")};
                }
                &:not(:disabled):hover > ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
                &:not(:disabled):focus > ${IconWrapper} {
                    color: ${color("common.ui10")};
                }
                &:disabled > ${IconWrapper} {
                    color: ${color("common.ui6")};
                    opacity: 0.33;
                }

                &,
                & > ${IconWrapper} {
                    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                }
            `,
        },
    },
    navIconsSrc: {
        firstPage: "ChevronsLeft",
        previousPage: PaginationArrowLeft,
        nextPage: PaginationArrowRight,
        lastPage: "ChevronsRight",
    },
} as xBaseTheme["pagination"];
